import { DateService, PulseWindow } from '@pulse/browser-shared';
import { AsyncTask } from '@pulse/common';

export interface StreamStats {
    size?: number | undefined;
    start: number;
    end: number;
    cancelled: boolean;
}

export function measureStreamSizeIfConsumed(
    stream: ReadableStream,
    date: DateService,
    win: PulseWindow,
) {
    const started = new AsyncTask<void>();
    const task = new AsyncTask<StreamStats>();

    let start = -1;
    const wrappedStream = new win.ReadableStream({
        start(controller) {
            start = date.hrt();
            const reader = stream.getReader();
            let size = 0;

            const pump = () => {
                reader
                    .read()
                    .then(({ done, value }) => {
                        if (done) {
                            const end = date.hrt();
                            task.resolve({ start, end, size, cancelled: false });
                            controller.close();

                            return;
                        }

                        try {
                            size += valueSize(value, win) ?? 0;
                        } catch (ex) {
                            console.error(ex);
                        }

                        controller.enqueue(value);
                    })
                    .catch((ex) => {
                        task.reject(ex);
                        controller.error(ex);
                    });
            };

            pump();
        },
        cancel(reason?: any) {
            stream.cancel(reason);
            const end = date.hrt();
            task.resolve({ start, end, cancelled: true });
        },
    });

    return {
        started: started.waitUntilResolved(),
        complete: task.waitUntilResolved(),
        wrappedStream,
    };
}

export function valueSize(
    v:
        | Document
        | Blob
        | BufferSource
        | FormData
        | URLSearchParams
        | string
        | Headers
        | Record<string, string>
        | null
        | undefined,
    win: PulseWindow,
): number | undefined {
    try {
        if (typeof v === 'undefined' || v === null || v === '') {
            return 0;
        }

        if (typeof v === 'string') {
            return new win.Blob([v]).size;
        }

        if (v instanceof win.Blob) {
            return v.size;
        }

        if (
            (v instanceof win.ArrayBuffer || win.ArrayBuffer.isView(v)) &&
            typeof v.byteLength === 'number'
        ) {
            return v.byteLength;
        }

        if (v instanceof win.URLSearchParams) {
            return valueSize(v.toString(), win);
        }

        if (typeof v === 'object') {
            return valueSize(JSON.stringify(v), win);
        }
        // eslint-disable-next-line no-empty
    } catch {}

    return undefined;
}
